import { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  IconButton,
  Modal,
  Typography,
  Stack,
  useTheme
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';

import PaidComponent from './pay';
import { getImageBasic } from '../../../../apiCalls';

// --- Hook to read ?imageId= from the URL query string ---
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

// --- Helper function to format date ---
const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

// --- The updated ViewImage component (no extra header or logo) ---
export const ViewImage = () => {
    const theme = useTheme();
    const [image, setImage] = useState(null);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { imageId } = useParams();

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');

    const handleOpen = (imagePath) => {
        setSelectedImage(imagePath);
        setModalOpen(true);
    };

    const handleClose = () => {
        setModalOpen(false);
    };

    // --- Fetch image data when the component mounts or imageId changes ---
    useEffect(() => {
        async function fetchImage() {
            try {
        const imageData = await getImageBasic(imageId);
                if (imageData) {
                    setImage(imageData);
                } else {
                    setError('Image data not found');
                }
            } catch (err) {
                setError('Failed to load image. Please try again later.');
            }
        }

        if (imageId) {
            fetchImage();
        }
    }, [imageId, navigate]);

    // --- Render states: loading, error, or the main content ---
    if (!image && !error) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    // --- Render the main image card and modal ---
    return (
        <Grid item m={2}>
            <Card
                sx={{
                    maxWidth: 800,
                    mb: 4,
                    backgroundColor: 'white',
                    boxShadow: theme.shadows[3]
                }}
            >
                {/* Main Image with Zoom functionality */}
                <Box sx={{ position: 'relative' }}>
                    <CardMedia
                        sx={{
                            height: 0,
                            paddingTop: '56.25%',
                            width: '100%',
                            cursor: 'pointer'
                        }}
                        image={image.path}
                        onClick={() => handleOpen(image.path)}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: theme.spacing(1),
                            right: theme.spacing(1),
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            borderRadius: '50%'
                        }}
                    >
                        <IconButton
                            color="primary"
                            aria-label="view image"
                            onClick={() => handleOpen(image.path)}
                        >
                            <ZoomInIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Image Details */}
                <CardContent>
                    <Stack spacing={1}>
                        {/* Image Metadata */}
                        <Box>
                            {/* Example layout for species, weight, area, date */}
                            <Typography variant="body2">
                                {image.species && `Species: ${image.species}`}
                            </Typography>
                            <Typography variant="body2">
                                {image.weight && `Weight: ${image.weight}`}
                            </Typography>
                            <Typography variant="body2">
                                {image.area && `Area: ${image.area}`}
                            </Typography>
                            <Typography variant="body2">
                                {image.dateTaken && `Caught on: ${formatDate(image.dateTaken)}`}
                            </Typography>
                        </Box>

                        {image.tags && (
                            <Box>
                                <Typography variant="body2">Tags: {image.tags}</Typography>
                            </Box>
                        )}

                        {/* PaidComponent if relevant */}
                        {image.publicise && <PaidComponent imageId={imageId} />}
                    </Stack>
                </CardContent>
            </Card>

            {/* Modal for full-size image view */}
            <Modal
                open={modalOpen}
                onClose={handleClose}
                aria-labelledby="image-modal-title"
                aria-describedby="image-modal-description"
            >
                <Box
                    sx={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90vw',
                        height: '90vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        outline: 'none'
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            maxWidth: '100%',
                            maxHeight: '100%'
                        }}
                    >
                        <img
                            src={selectedImage}
                            alt="Full Size"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '90vh',
                                objectFit: 'contain',
                                borderRadius: '12px'
                            }}
                        />
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            sx={{
                                position: 'absolute',
                                top: -20,
                                right: -20,
                                backgroundColor: 'background.paper',
                                boxShadow: 2,
                                '&:hover': {
                                    backgroundColor: 'background.paper',
                                    opacity: 0.9
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Modal>
        </Grid>
    );
};

export default ViewImage;
